import { ActionTree } from 'vuex'
import { RootState } from '@/store'
import { DealState, emptyOwner } from './index'
import bookingRequests from '@/requests/app/booking'
import {
  BookingV1DealDocumentListRequest, Deal,
  BookingV1DealOwnerDocumentCreateRequest, BookingV1DealOwnerDocumentPartialUpdateRequest,
  BookingV1DealPartialUpdateRequest, ChangeOwner, Document, DocumentTypeEnum, Owner,
  Owners, BookingV1DealNoteCreateRequest, BookingV1ContractDateCreateRequest,
  BookingV1DealDocumentCreateRequest, InlineResponse2006, BookingV1DealDocumentDeleteRequest,
  BookingV1DealDocumentPartialUpdateRequest, DocumentChange, OwnersTypeEnum
} from '@/requests/mainApi'
import {
  IAttachment, IFillOwnersArrayVariables, createOwnerPayload, updateOwnerPayload
} from '@/views/App/Deal/store/interfaces'
import userInfoRequests from '@/requests/app/userInfo'
import uploadRequest from '@/requests/app/upload'
import {
  AttachmentsV1IdGetRequest, AttachmentsV1PostRequest, AttachmentsV1PostXAttachmentTypeEnum,
  Attachment
} from '@/requests/imagesService'
import { IResponse } from '@/requests/requestMachine'
import i18n from '@/lib/i18n'

const documentTypes: AttachmentsV1PostXAttachmentTypeEnum[] = [
  AttachmentsV1PostXAttachmentTypeEnum.MortgageCertificate,
  AttachmentsV1PostXAttachmentTypeEnum.SubsidyCertificate,
  AttachmentsV1PostXAttachmentTypeEnum.MaternalCapitalCertificate,
  AttachmentsV1PostXAttachmentTypeEnum.MaternalCapitalCertificateBalance,
  AttachmentsV1PostXAttachmentTypeEnum.MarriageContract,
  AttachmentsV1PostXAttachmentTypeEnum.SpousesNotarialConsent
]

export const actions: ActionTree<DealState, RootState> = {
  async init ({ dispatch }) {
    await dispatch('getOwnerships')
    await Promise.all([dispatch('getOwners'), dispatch('getOwnerDocuments', {})])
  },
  async getOwnerships ({ commit, getters }) {
    try {
      const response = await bookingRequests.getOwnershipsList()
      commit('setOwnerships', response.body)
    } catch (e) {
      console.error(e)
    }
  },
  async getOwners ({ getters, dispatch }) {
    try {
      const response = await bookingRequests.getOwnerList({})
      dispatch('fillOwnersArray', {
        count: getters.currentOwnership ? getters.currentOwnership.min : 0,
        exist: response.body!.results
      })
    } catch (e) {
      console.error(e)
    }
  },
  fillOwnersArray ({ commit }, payload: IFillOwnersArrayVariables) {
    if (payload.count < payload.exist.length) {
      commit('setOwners', payload.exist)
      return
    }

    const Owners: Owners[] = [...payload.exist]

    for (let i = Owners.length; i < payload.count; i++) {
      Owners.push({ ...emptyOwner })
    }

    commit('setOwners', Owners)
  },
  async createOwner ({ commit, getters, rootGetters }, payload: createOwnerPayload): Promise<void> {
    try {
      const ownerResponse = await bookingRequests.createOwner(payload.model)
      const ownerType: Owner = getters.currentOwnership.ownerTypes
        .find((owner: Owner) => owner.type === payload.model.data.type)!
      const requiredDocuments = ownerType.documents as DocumentTypeEnum[]
      const optionalDocuments = ownerType.type === OwnersTypeEnum.Spouse
        ? [DocumentTypeEnum.MarriageContract]
        : null
      const neededDocuments: DocumentTypeEnum[] = [...requiredDocuments, ...optionalDocuments || []]
      const requestsToCreateDocuments =
        neededDocuments.map((document:DocumentTypeEnum) => {
          return bookingRequests.createOwnerDocuments({
            data: {
              deal: rootGetters.deal.id,
              type: document,
              owner: ownerResponse.body!.id
            }
          })
        })
      const documents: IResponse<Document>[] = await Promise.all(requestsToCreateDocuments)
      commit('setOwners', getters.owners.map((owner: Owners, index: number) => {
        if (index === payload.index) return ownerResponse.body
        return owner
      }))
      commit('setOwnerDocumentsList', [
        ...getters.ownerDocumentsList,
        ...documents.map((document) => {
          return document.body!
        })
      ])
    } catch (e) {
      console.error(e)
    }
  },
  async updateOwner ({ commit, getters }, payload: updateOwnerPayload): Promise<void> {
    try {
      await bookingRequests.updateOwner({
        id: payload.model!.data!.id!,
        data: payload.model.data as ChangeOwner
      })
    } catch (e) {
      console.error(e)
    }
  },
  async deleteOwners ({ getters, commit }, ownerId?: number): Promise<void> {
    try {
      if (ownerId) {
        await bookingRequests.deleteOwner({
          id: ownerId
        })
        return
      }
      await Promise.all(getters.owners.reduce((acc: Promise<IResponse<void>>[], cur: Owners) => {
        if (cur.id) {
          acc.push(bookingRequests.deleteOwner({
            id: cur.id
          }))
        }
        return acc
      }, []))
      commit('setOwnerDocumentsList', [])
    } catch (e) {
      console.error(e)
    }
  },
  async getDeal ({ commit }) {
    try {
      const response = await bookingRequests.getDeal()
      commit('setDeal', response.body, { root: true })
    } catch (e) {
      console.error(e)
    }
  },
  async updateDeal ({ commit, dispatch, getters }, payload: BookingV1DealPartialUpdateRequest) {
    try {
      const response = await bookingRequests.updateDeal(payload)
      commit('setDeal', response.body, { root: true })
      await dispatch('fillOwnersArray', {
        count: getters.currentOwnership ? getters.currentOwnership.min : 0,
        exist: []
      })
    } catch (e) {
      console.error(e)
    }
  },
  async getOwnerDocuments ({ commit }, payload: BookingV1DealDocumentListRequest) {
    try {
      const response = await bookingRequests.getOwnerDocumentList(payload)
      commit('setOwnerDocumentsList', response.body!.results)
    } catch (e) {
      console.error(e)
    }
  },
  async createOwnerDocuments ({ commit, getters }, payload: BookingV1DealOwnerDocumentCreateRequest) {
    try {
      const response = await bookingRequests.createOwnerDocuments(payload)
      commit('setOwnerDocumentsList', [...getters.ownerDocumentsList, response.body])
    } catch (e) {
      console.error(e)
    }
  },
  async updateOwnerDocuments ({ commit, dispatch }, payload: BookingV1DealOwnerDocumentPartialUpdateRequest): Promise<boolean> {
    const response = await bookingRequests.updateOwnerDocuments(payload)
    const statusSuccess = Boolean(response.status || response.code !== 400)
    if (!statusSuccess) {
      if (payload.data.type! as unknown as DocumentTypeEnum === DocumentTypeEnum.Snils) {
        commit('ui/setNotifications', [{
          id: `${Math.random()}-${Date.now()}`,
          message: (i18n.messages.ru.app as any).ownershipDetails.snilsControlSumError
        }], { root: true })
      } else if (payload.data.type! as unknown as DocumentTypeEnum === DocumentTypeEnum.Inn) {
        commit('ui/setNotifications', [{
          id: `${Math.random()}-${Date.now()}`,
          message: (i18n.messages.ru.app as any).ownershipDetails.innControlSumError
        }], { root: true })
      }
    }
    return statusSuccess
  },
  async loadOwnerDocument  ({ getters, commit, dispatch }, payload: AttachmentsV1PostRequest): Promise<void> {
    try {
      await uploadRequest.loadPicture(payload)
      await dispatch('getOwnerDocuments', {})
    } catch (e) {
      console.error(e)
    }
  },
  async sendOwnersToAmo (): Promise<boolean> {
    try {
      const response = await bookingRequests.sendOwnersToAmo()
      return response.status
    } catch (e) {
      console.error(e)
      return false
    }
  },
  async getPaymentDetails ({ commit }): Promise<object | undefined> {
    try {
      const response = await bookingRequests.getPaymentDetails()
      commit('setPaymentDetails', response.body)
      return response.body
    } catch (e) {
      console.error(e)
    }
  },
  async getPaymentDocuments ({ commit }, payload: BookingV1DealDocumentListRequest): Promise<InlineResponse2006 | undefined> {
    try {
      const response = await bookingRequests.getPaymentDocuments(payload)
      commit('setDealDocuments', response.body!.results)
      return response.body
    } catch (e) {
      console.error(e)
    }
  },
  async createPaymentDocuments ({ commit, getters, dispatch }, payload: BookingV1DealDocumentCreateRequest) {
    try {
      const response = await bookingRequests.createPaymentDocuments(payload)
      if (!response.status) {
        dispatch('ui/addNotification', {
          message: (i18n.messages.ru.app as any).paymentDetails.errorSendingDocs
        }, { root: true })
      }
      commit('setDealDocuments', [...getters.dealDocuments, response.body])
      return response.body
    } catch (e) {
      console.error(e)
    }
  },
  async updatePaymentDocuments ({ commit, dispatch }, payload: BookingV1DealDocumentPartialUpdateRequest): Promise<DocumentChange | undefined> {
    try {
      const response = await bookingRequests.updatePaymentDocuments(payload)
      if (!response.status) {
        dispatch('ui/addNotification', {
          message: (i18n.messages.ru.app as any).paymentDetails.errorSendingDocs
        }, { root: true })
      }
      return response.body!
    } catch (e) {
      console.error(e)
    }
  },
  async loadPaymentDocument ({ getters, commit, dispatch }, payload: AttachmentsV1PostRequest): Promise<void> {
    try {
      await uploadRequest.loadPicture(payload)
      await dispatch('getPaymentDocuments', {})
    } catch (e) {
      console.error(e)
    }
  },
  async deletePaymentDocuments ({ commit, getters }, payload: BookingV1DealDocumentDeleteRequest) {
    try {
      await bookingRequests.deletePaymentDocuments(payload)
      commit('setDealDocuments', getters.dealDocuments.filter((doc: Document) => doc.id !== payload.id))
    } catch (e) {
      console.error(e)
    }
  },
  async sendPaymentDetails (): Promise<void> {
    try {
      await bookingRequests.sendPaymentDetails()
    } catch (e) {
      console.error(e)
    }
  },
  async getContract ({ commit }): Promise<Blob | undefined> {
    try {
      const response = await bookingRequests.getContract()
      commit('setContract', response.body)
      return response.body
    } catch (e) {
      console.error(e)
    }
  },
  async commentOnContract ({ commit, dispatch }, payload: BookingV1DealNoteCreateRequest): Promise<Deal | undefined> {
    try {
      const response = await bookingRequests.commentOnContract(payload)
      if (response.status) {
        dispatch('ui/addNotification', {
          message: (i18n.messages.ru.app as any).contractDetails.commentSent
        }, { root: true })
      }
      return response.body
    } catch (e) {
      console.error(e)
    }
  },
  async agreeOnContract (): Promise<void> {
    try {
      await bookingRequests.agreeOnContract()
    } catch (e) {
      console.error(e)
    }
  },
  async loadDocument ({ getters, commit, dispatch }, payload: AttachmentsV1PostRequest): Promise<void> {
    try {
      const response = await uploadRequest.loadPicture(payload)

      const src = await dispatch('getDocumentLink', { id: response.body!.result?.id })

      const { docsList } = getters
      if (docsList.find((item: IAttachment) => item.type === response.body!.result?.type)) {
        commit('setDocsList', docsList.map((item: IAttachment) => {
          return item.type === response.body!.result?.type && response.body!.result
            ? {
              ...response.body!.result,
              src
            }
            : item
        }))
      } else {
        commit('setDocsList', [
          ...docsList,
          {
            ...response.body!.result,
            src
          }
        ])
      }
    } catch (e) {
      console.error(e)
    }
  },
  async getUserDocuments ({ commit, dispatch }): Promise<void> {
    try {
      const response = await userInfoRequests.getUserDocuments({})

      let foundItem
      const currentScans = documentTypes.reduce((acc: Attachment[], curr: AttachmentsV1PostXAttachmentTypeEnum) => {
        foundItem = response.body!.results.find(docs => docs.type as any === curr)

        if (foundItem) (acc as any).push(foundItem)

        return acc
      }, [] as Attachment[])

      const pictures = await Promise.all(currentScans
        .map(item => dispatch('getDocumentLink', { id: item.id }))
      )

      commit('setDocsList', currentScans.map((item, index) => {
        return {
          ...item,
          src: pictures[index]
        }
      }))
    } catch (e) {
      console.error(e)
    }
  },
  async getDocumentLink ({ commit }, payload: AttachmentsV1IdGetRequest): Promise<string | undefined> {
    try {
      const response = await uploadRequest.getPictureById(payload)
      if (response.code === 404) return undefined
      return URL.createObjectURL(response.body)
    } catch (e) {
      console.error(e)
    }
  },
  async sendTypeOfSigning ({ commit }, payload: BookingV1ContractDateCreateRequest): Promise<void> {
    try {
      await bookingRequests.sendTypeOfSigning(payload)
    } catch (e) {
      console.error(e)
    }
  }
}
